'use client';

import React, { forwardRef, useEffect } from 'react';
import Link from 'next/link';
import useSWR from 'swr';
import { getCartCount } from 'apis/cart';
import { Button } from '@shared/ui';
import { FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { type BasicShopUIPropTypes, type BuyerLayoutProps } from 'shop-ui/types';
import { CACHE_QUERY_KEY } from 'app/constants';
import { headerCartIconMap, HEADER_CART_ICON_KEY } from './Icon';
import './HeaderCartIcon.scss';

interface HeaderCartIconProps extends BasicShopUIPropTypes, BuyerLayoutProps {
    content: string;
}

export const HeaderCartIcon = forwardRef<HTMLDivElement, HeaderCartIconProps>(
    (props, ref) => {
        const { style, isEditingMode, styleMap, isBuyer } = props;

        const { data: cartCount } = useSWR(
            CACHE_QUERY_KEY.buyerCartCount,
            isBuyer ? getCartCount : null,
            {
                refreshInterval: 1000 * 60 * 5,
            }
        );

        useEffect(() => {
            const element = ref as React.RefObject<HTMLDivElement>;

            if (element?.current && typeof window !== 'undefined' && window) {
                const headerContainer = element.current.closest(
                    '[data-shop-component-header]'
                ) as HTMLElement;

                const cell = element.current.closest(
                    '[data-shop-component-grid-cell]'
                ) as HTMLElement;

                if (headerContainer && cell) {
                    cell.parentElement?.classList.add('header-cart-icon--responsive');
                }
            }
        }, [ref]);

        return (
            <div ref={ref} style={style} className="header-cart-icon">
                <Button
                    variant="plain"
                    as={Link}
                    // @ts-ignore
                    href="/cart"
                    sx={{
                        pointerEvents: isEditingMode ? 'none' : 'auto',
                        color: 'var(--shop-ui-custom-color,rgba(57, 57, 62, 1))',
                        p: '0 !important',
                    }}
                    position="relative"
                    inline
                >
                    {!!cartCount?.data?.count && (
                        <div className="cart-count">{cartCount.data.count}</div>
                    )}
                    {
                        headerCartIconMap[
                            styleMap?.[
                                FEATURE_FLAG_KEYS.HEADER_ICON_STYLE
                            ] as HEADER_CART_ICON_KEY
                        ]
                    }
                </Button>
            </div>
        );
    }
);

HeaderCartIcon.displayName = 'HeaderCartIcon';
